import type { TokenRepository } from "@/domains/tokens"
import { LcAddress } from "@/domains/core/lc-address"

import type { ClFarmApiData, ClFarmStoreDAO } from "./types"

/**
 * Convert API response to CL Farm Store DAO format
 * Maps ClsetResponseItem to ClFarmStoreDAO structure
 * Converts string values to numbers where needed
 * Finds actual TokenStore objects from the token repository
 */
export function convertApiResponseToClFarmStoreDAO(
  apiResponse: ClFarmApiData,
  tokenRepository: TokenRepository,
): ClFarmStoreDAO {
  // console.log("apiResponse", apiResponse)

  // if (apiResponse.integration.equalizerPair.address.toLowerCase() === "0x7163cb15f7419a22ea0cea04054e7508ff31d878") {
  //   console.log("apiResponse", apiResponse)
  // }

  // Find tokens in the repository
  const token0 = tokenRepository.find(new LcAddress(apiResponse.token0.address))
  const token1 = tokenRepository.find(new LcAddress(apiResponse.token1.address))
  const tokenEarn = tokenRepository.find(new LcAddress(apiResponse.tokenEarn.address))

  // Log errors for missing tokens
  if (!token0) {
    console.error(`Token0 not found for CL farm "${apiResponse.name}": ${apiResponse.token0.address}`)
  }
  if (!token1) {
    console.error(`Token1 not found for CL farm "${apiResponse.name}": ${apiResponse.token1.address}`)
  }
  if (!tokenEarn) {
    console.error(`TokenEarn not found for CL farm "${apiResponse.name}": ${apiResponse.tokenEarn.address}`)
  }

  // Skip conversion if any required token is missing
  if (!token0 || !token1 || !tokenEarn) {
    throw new Error(`Missing tokens for CL farm "${apiResponse.name}"`)
  }

  const mintTokens = apiResponse.config.mintTokens
    ?.map((mintToken) => {
      const token = tokenRepository.find(new LcAddress(mintToken.address))
      return token
        ? {
            minimumAmount: mintToken.minimumAmount,
            token,
          }
        : null
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  const providerLp = {
    address: new LcAddress(apiResponse.config.token.address),
    symbol: apiResponse.config.token.symbol,
    decimals: apiResponse.config.token.decimals,
  }

  const farmAddress = new LcAddress(apiResponse.integration.equalizerPair.address)
  const gaugeAddress = apiResponse.integration.gauge?.address
    ? new LcAddress(apiResponse.integration.gauge.address)
    : null

  return {
    address: farmAddress,
    alm: apiResponse.alm,
    analytics: apiResponse.info.analytics,
    apr: apiResponse.integration?.gauge?.apr ?? 0,
    bribeAddress: apiResponse.integration.bribes ? new LcAddress(apiResponse.integration.bribes) : null,
    buck1: apiResponse.config.buck1,
    clPoolAddress: new LcAddress(apiResponse.integration.clPool),
    decimals: apiResponse.config.token.decimals,
    description: apiResponse.description,
    dex: apiResponse.dex,
    displayName: apiResponse.integration.equalizerPair.displayName,
    displayType: apiResponse.integration.equalizerPair.displayType,
    docs: apiResponse.info.docs,
    extendedType: apiResponse.integration.equalizerPair.extendedType,
    farmAddress: new LcAddress(apiResponse.config.farm),
    fee: 0, // @todo update the API to include the fee
    feeAddress: apiResponse.integration.fees ? new LcAddress(apiResponse.integration.fees) : null,
    flipLensTokens: apiResponse.config.flipLensTokens,
    gaugeAddress,
    guardAddress: apiResponse.config.guard ? new LcAddress(apiResponse.config.guard) : undefined,
    isProductionReady: apiResponse.isProductionReady,
    isSingleSided: apiResponse.isSingleSided,
    isStable: apiResponse.integration.isStable,
    mintTokens,
    name: apiResponse.name,
    pool: apiResponse.config.pool,
    providerApp: apiResponse.info.providerApp,
    providerLp,
    slipBps: apiResponse.config.slipBps,
    tags: [...apiResponse.tags, ...apiResponse.integration.equalizerPair.tags],
    token0,
    token1,
    tokenEarn,
    vaultAddress: new LcAddress(apiResponse.config.vault),
    vdep: apiResponse.config.vdep,
    voterAddress: new LcAddress(apiResponse.config.voter),
  }
}
